import type {
    AgentInteractionState,
    OrchestratorState,
    ReasoningStep,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React, { useState } from 'react';
import AgentCard from './AgentCard';

interface OrchestrationAgentsPanelProps {
  agents: SimpleAgentConfig;
  agentInteractions: Map<string, AgentInteractionState>;
  orchestratorState: OrchestratorState;
  currentReasoning: ReasoningStep[];
  activeAgents: string[];
  primaryColor?: string;
}

const OrchestrationAgentsPanel: React.FC<OrchestrationAgentsPanelProps> = ({
  agents,
  agentInteractions,
  orchestratorState,
  currentReasoning,
  activeAgents,
  primaryColor = '#3498db',
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'complete' | 'idle'>('all');

  // Filter agents based on status
  const filteredAgents = agents.filter(agent => {
    if (filterStatus === 'all') return true;
    
    const interaction = agentInteractions.get(agent.id);
    if (!interaction) return filterStatus === 'idle';
    
    switch (filterStatus) {
      case 'active':
        return interaction.status === 'processing' || interaction.status === 'called' || interaction.status === 'responding';
      case 'complete':
        return interaction.status === 'complete';
      case 'idle':
        return interaction.status === 'idle';
      default:
        return true;
    }
  });

  // Sort agents by orchestration priority
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    const aInteraction = agentInteractions.get(a.id);
    const bInteraction = agentInteractions.get(b.id);
    
    // Active agents first
    if (aInteraction?.status === 'processing' && bInteraction?.status !== 'processing') return -1;
    if (bInteraction?.status === 'processing' && aInteraction?.status !== 'processing') return 1;
    
    // Then by call priority if available
    const aPriority = a.callPriority || 999;
    const bPriority = b.callPriority || 999;
    
    return aPriority - bPriority;
  });

  const getStatusCounts = () => {
    const counts = {
      active: 0,
      complete: 0,
      idle: 0,
      total: agents.length,
    };

    agents.forEach(agent => {
      const interaction = agentInteractions.get(agent.id);
      if (!interaction || interaction.status === 'idle') {
        counts.idle++;
      } else if (interaction.status === 'complete') {
        counts.complete++;
      } else {
        counts.active++;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header with Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '15px',
        paddingBottom: '12px',
        borderBottom: `2px solid ${primaryColor}`,
        flexShrink: 0,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '1.4em' }}>🤖</span>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            Specialized Agents
          </h3>
          <div style={{
            background: `${primaryColor}20`,
            color: primaryColor,
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.8em',
            fontWeight: 'bold',
          }}>
            {statusCounts.total}
          </div>
        </div>

        {/* View Mode Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <button
            onClick={() => setViewMode('list')}
            style={{
              background: viewMode === 'list' ? primaryColor : 'transparent',
              color: viewMode === 'list' ? 'white' : '#666',
              border: `1px solid ${viewMode === 'list' ? primaryColor : '#ddd'}`,
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8em',
              fontWeight: 'bold',
            }}
          >
            📋 List
          </button>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              background: viewMode === 'grid' ? primaryColor : 'transparent',
              color: viewMode === 'grid' ? 'white' : '#666',
              border: `1px solid ${viewMode === 'grid' ? primaryColor : '#ddd'}`,
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8em',
              fontWeight: 'bold',
            }}
          >
            ⚏ Grid
          </button>
        </div>
      </div>

      {/* Status Filter Bar */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '15px',
        flexWrap: 'wrap',
        flexShrink: 0,
      }}>
        {[
          { key: 'all', label: 'All', count: statusCounts.total, color: '#6c757d' },
          { key: 'active', label: 'Active', count: statusCounts.active, color: '#f39c12' },
          { key: 'complete', label: 'Complete', count: statusCounts.complete, color: '#27ae60' },
          { key: 'idle', label: 'Idle', count: statusCounts.idle, color: '#95a5a6' },
        ].map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setFilterStatus(key as any)}
            style={{
              background: filterStatus === key ? `${color}20` : 'transparent',
              color: filterStatus === key ? color : '#666',
              border: `1px solid ${filterStatus === key ? color : '#ddd'}`,
              padding: '6px 12px',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '0.8em',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {label}
            <span style={{
              background: filterStatus === key ? color : '#ddd',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '10px',
              fontSize: '0.75em',
              minWidth: '18px',
              textAlign: 'center',
            }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Orchestration Status Summary */}
      {orchestratorState.status !== 'ready' && (
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '15px',
          fontSize: '0.85em',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '6px',
          }}>
            <span style={{ fontSize: '1.1em' }}>
              {orchestratorState.status === 'processing' ? '⚙️' : 
               orchestratorState.status === 'synthesizing' ? '🧠' : '🔍'}
            </span>
            <strong style={{ color: '#2c3e50' }}>
              Orchestration Status: {orchestratorState.status.replace('_', ' ')}
            </strong>
          </div>
          {orchestratorState.currentStep && (
            <div style={{ color: '#666', lineHeight: '1.4' }}>
              {orchestratorState.currentStep.description}
            </div>
          )}
        </div>
      )}

      {/* Agents Display */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        paddingRight: '5px',
        marginRight: '-5px',
      }}>
        {viewMode === 'grid' ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '15px',
          }}>
            {sortedAgents.map((agent) => {
              const reasoning = currentReasoning.find(r =>
                r.agent.includes(agent.name.split(' ')[0]) ||
                r.agent.includes(agent.name.split(' ')[1])
              );
              const interaction = agentInteractions.get(agent.id);

              return (
                <div key={agent.id} style={{ position: 'relative' }}>
                  <AgentCard
                    agent={agent}
                    isActive={activeAgents.includes(agent.id)}
                    reasoning={reasoning}
                    primaryColor={primaryColor}
                  />
                  {/* Orchestration Status Overlay */}
                  {interaction && interaction.status !== 'idle' && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: 'white',
                      border: `2px solid ${primaryColor}`,
                      borderRadius: '20px',
                      padding: '4px 8px',
                      fontSize: '0.7em',
                      fontWeight: 'bold',
                      color: primaryColor,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                      {interaction.status === 'processing' && `${Math.round(interaction.progress)}%`}
                      {interaction.status === 'complete' && '✅'}
                      {interaction.status === 'called' && '📞'}
                      {interaction.status === 'responding' && '💬'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sortedAgents.map((agent) => {
              const reasoning = currentReasoning.find(r =>
                r.agent.includes(agent.name.split(' ')[0]) ||
                r.agent.includes(agent.name.split(' ')[1])
              );
              const interaction = agentInteractions.get(agent.id);

              return (
                <div key={agent.id} style={{ position: 'relative' }}>
                  <AgentCard
                    agent={agent}
                    isActive={activeAgents.includes(agent.id)}
                    reasoning={reasoning}
                    primaryColor={primaryColor}
                  />
                  {/* Enhanced Orchestration Info for List View */}
                  {interaction && interaction.status !== 'idle' && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: `${primaryColor}10`,
                      border: `1px solid ${primaryColor}30`,
                      borderRadius: '8px',
                      fontSize: '0.8em',
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}>
                        <span style={{ fontWeight: 'bold', color: primaryColor }}>
                          Orchestration Status: {interaction.status}
                        </span>
                        {interaction.progress > 0 && (
                          <span style={{ color: '#666' }}>
                            {Math.round(interaction.progress)}%
                          </span>
                        )}
                      </div>
                      {interaction.currentAction && (
                        <div style={{ color: '#666', fontSize: '0.9em' }}>
                          Current Action: {interaction.currentAction}
                        </div>
                      )}
                      {interaction.toolsInUse.length > 0 && (
                        <div style={{ color: '#666', fontSize: '0.9em' }}>
                          Tools: {interaction.toolsInUse.join(', ')}
                        </div>
                      )}
                      {interaction.progress > 0 && (
                        <div style={{
                          marginTop: '6px',
                          height: '4px',
                          background: '#e9ecef',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${interaction.progress}%`,
                            height: '100%',
                            background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}aa)`,
                            borderRadius: '2px',
                            transition: 'width 0.5s ease',
                          }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {sortedAgents.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#666',
          }}>
            <div style={{ fontSize: '3em', marginBottom: '15px' }}>🔍</div>
            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '8px' }}>
              No agents match the current filter
            </div>
            <div style={{ fontSize: '0.9em' }}>
              Try selecting a different status filter to see more agents.
            </div>
          </div>
        )}
      </div>

      {/* Footer with Quick Stats */}
      <div style={{
        marginTop: '15px',
        paddingTop: '12px',
        borderTop: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.8em',
        color: '#666',
        flexShrink: 0,
      }}>
        <div>
          Showing {sortedAgents.length} of {agents.length} agents
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <span>🟢 {statusCounts.complete} Complete</span>
          <span>🟡 {statusCounts.active} Active</span>
          <span>⚪ {statusCounts.idle} Idle</span>
        </div>
      </div>
    </div>
  );
};

export default OrchestrationAgentsPanel;